import { motion } from "framer-motion";
import { BadgeCheck, Grid3x3, Bookmark, UserSquare2 } from "lucide-react";
import { FL } from "@/constants/testIds/fairylights";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function ProfileHeader({ profile, onFollow, onMessage, onContact }) {
  if (!profile) return null;
  const { handle, displayName, tagline, bio, location, website, avatar } = profile;

  return (
    <section id="profile" className="relative bg-[#4A0E1F] px-6 pt-24 sm:px-10 md:px-14 md:pt-32">
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="mx-auto max-w-5xl"
      >
        <motion.div variants={item} className="mb-10 flex items-center gap-3">
          <span className="h-px w-10 bg-[#D4AF37]" />
          <span className="font-body text-[11px] uppercase tracking-[0.36em] text-[#D4AF37]">
            The Profile
          </span>
        </motion.div>

        <div className="grid grid-cols-1 gap-10 md:grid-cols-[220px_1fr] md:items-start md:gap-16">
          {/* Avatar */}
          <motion.div variants={item} className="relative flex justify-center md:justify-start">
            <div className="gold-ring w-[180px] md:w-[200px]">
              <div className="relative overflow-hidden rounded-full bg-[#1A050B]">
                <img
                  src={avatar}
                  alt={`${displayName} avatar`}
                  className="aspect-square w-full object-cover"
                />
              </div>
            </div>
            <span className="sparkle" style={{ top: "8%", right: "8%" }} />
            <span className="sparkle" style={{ bottom: "12%", left: "6%", animationDelay: "1.2s" }} />
          </motion.div>

          {/* Info */}
          <div>
            <motion.div variants={item} className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-3">
              <h2 className="font-body text-[20px] font-light tracking-wide text-[#FDFBF7]">
                @{handle}
                <BadgeCheck className="ml-2 inline-block h-4 w-4 text-[#D4AF37]" />
              </h2>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid={FL.followBtn}
                  onClick={onFollow}
                  className="pill pill-primary"
                >
                  Follow
                </button>
                <button
                  data-testid={FL.messageBtn}
                  onClick={onMessage}
                  className="pill pill-ghost"
                >
                  Message
                </button>
                <button
                  data-testid={FL.contactBtn}
                  onClick={onContact}
                  className="pill pill-ghost"
                >
                  Contact
                </button>
              </div>
            </motion.div>

            {/* Bio */}
            <motion.div variants={item} className="mt-6">
              <div className="font-serif text-3xl leading-[1.15] tracking-tight text-[#FDFBF7] md:text-4xl">
                {displayName}
              </div>
              <div className="mt-1 font-italic-serif text-lg text-[#D4AF37]">
                {tagline}
              </div>
              <div className="mt-4 space-y-1 font-body text-[14px] leading-[1.7] text-[#FDFBF7]/80">
                {bio.map((b, i) => (
                  <div key={i}>{b}</div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-4 font-body text-[13px] text-[#D4AF37]">
                <a
                  href={`https://${website}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-4 decoration-[#D4AF37]/50 hover:decoration-[#D4AF37] transition-colors"
                >
                  {website}
                </a>
                <span className="text-[#FDFBF7]/40">·</span>
                <span className="text-[#FDFBF7]/70">{location}</span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Grid tabs (visual only, like IG) */}
        <motion.div
          variants={item}
          className="mt-16 grid grid-cols-3 border-t border-[#D4AF37]/25 text-[11px] uppercase tracking-[0.28em] text-[#FDFBF7]/60"
        >
          <div className="flex items-center justify-center gap-2 border-t border-[#D4AF37] py-4 text-[#D4AF37]">
            <Grid3x3 className="h-3.5 w-3.5" /> Posts
          </div>
          <div className="flex items-center justify-center gap-2 py-4">
            <Bookmark className="h-3.5 w-3.5" /> Saved
          </div>
          <div className="flex items-center justify-center gap-2 py-4">
            <UserSquare2 className="h-3.5 w-3.5" /> Tagged
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
